import { PagedRecords } from '@frk/eds-components';
import ppssAvAnnualMonthlyQuery from '@graphql/fund-listing/av-annual-monthly.graphql';
import ppssAvAnnualQuarterlyQuery from '@graphql/fund-listing/av-annual-quarterly.graphql';
import ppssCalendarYearMonthlyQuery from '@graphql/fund-listing/calendar-year-monthly.graphql';
import ppssCalendarYearQuarterlyQuery from '@graphql/fund-listing/calendar-year-quarterly.graphql';
import ppssCommonPerformanceQuery from '@graphql/fund-listing/common-performance.graphql';
import ppssCumulativeMonthlyQuery from '@graphql/fund-listing/cumulative-monthly.graphql';
import ppssCumulativeQuarterlyQuery from '@graphql/fund-listing/cumulative-quarterly.graphql';
import ppssDiscreteMonthlyQuery from '@graphql/fund-listing/discrete-monthly.graphql';
import ppssDiscreteQuarterlyQuery from '@graphql/fund-listing/discrete-quarterly.graphql';
import ppssInternationalCoreQuery from '@graphql/fund-listing/international-core.graphql';
import ppssInternationalIdentifiersQuery from '@graphql/fund-listing/international-identifiers.graphql';
import ppssInternationalRatingsQuery from '@graphql/fund-listing/international-ratings.graphql';
import ppssUs529Query from '@graphql/fund-listing/us-529.graphql';
import ppssUsCefQuery from '@graphql/fund-listing/us-cef.graphql';
import ppssUsCoreQuery from '@graphql/fund-listing/us-core.graphql';
import ppssUsIndentifiers from '@graphql/fund-listing/us-identifiers.graphql';
import ppssUsRatingsQuery from '@graphql/fund-listing/us-ratings.graphql';
import ppssUsSmaQuery from '@graphql/fund-listing/us-sma.graphql';
import ppssUsVipQuery from '@graphql/fund-listing/us-vip.graphql';
import ppssUsYieldsQuery from '@graphql/fund-listing/us-yields.graphql';
import { Product } from '@models';
import { ProductDetailService } from '@products/product-detail-layout/product-detail.service';
import { ProductTypeParameter } from '@products/services/graphql-fund-data.service';
import {
  DebugService,
  SegmentService,
  SiteConfigService,
  StorageService,
} from '@services';
import {
  ConfigurationId,
  FundId,
  LiteratureDocType,
  LiteratureDocument,
  PPSSState,
  ProductData,
  ShareClassCode,
} from '@types';
import { Logger } from '@utils/logger';
import { getFileNameFromUrl } from '@utils/text/string-utils';
import { DocumentNode } from 'graphql';
import { BehaviorSubject, Observable } from 'rxjs';
import { FundFilterService } from './fund-filter.service';

const DEFAULT_PPSS_PAGE_SIZE = 25;

const logger = Logger.getLogger('BasePpssService');
const QUERIES = {
  DISCRETE_MONTHLY: ppssDiscreteMonthlyQuery,
  DISCRETE_QUARTERLY: ppssDiscreteQuarterlyQuery,
  CUMULATIVE_MONTHLY: ppssCumulativeMonthlyQuery,
  CUMULATIVE_QUARTERLY: ppssCumulativeQuarterlyQuery,
  CALENDAR_YEAR_MONTHLY: ppssCalendarYearMonthlyQuery,
  CALENDAR_YEAR_QUARTERLY: ppssCalendarYearQuarterlyQuery,
  AVG_ANNUAL_MONTHLY: ppssAvAnnualMonthlyQuery,
  AVG_ANNUAL_QUARTERLY: ppssAvAnnualQuarterlyQuery,
};

/**
 * Common functionality for all PPSS components
 */
export class BasePpssService {
  private subject: BehaviorSubject<PPSSState>;
  protected pagedFunds: PagedRecords<ProductData>;
  public isSiteIntl: boolean;
  public _isFilterProcessing = false;
  public primaryShareClassMap = new Map<string, string>();
  constructor(
    protected productType: ConfigurationId,
    protected state: PPSSState,
    protected filterService: FundFilterService,
    protected siteConfigService: SiteConfigService,
    protected segmentService: SegmentService,
    protected storageService: StorageService,
    protected debugService: DebugService,
    protected productDetailService: ProductDetailService
  ) {
    this.subject = new BehaviorSubject<PPSSState>(this.state);

    this.pagedFunds = new PagedRecords<ProductData>(DEFAULT_PPSS_PAGE_SIZE);
    // check storage for previously set pageSize
    this.storageService
      .retrieve<number>('ppss_page_size')
      .then((pageSize: number): void => {
        logger.debug(
          'stored ppss_page_size',
          pageSize,
          typeof pageSize === 'number'
        );
        if (typeof pageSize === 'number') {
          this.pagedFunds.pageSize = pageSize;
        }
      });
    this.isSiteIntl = this.siteConfigService.product?.site?.country !== 'US'; // Set the site international flag value accordingly.
    this.filterService.getData().subscribe(([dataType, data]) => {
      if (dataType === 'filteredProducts') {
        this.filterProducts(data);
      }
    });
    segmentService.getCurrentSegmentId$().subscribe(() => {
      this.state.data.tableRows = [];
      this.state.data.filteredTableRows = [];
      this.state.hasLoadedFullFeed = false;
    });
    this.debugService
      .getExpandAllComponents$()
      .subscribe((expandAll: boolean): void => {
        if (expandAll) {
          this.pagedFunds.expandAll();
        }
      });
  }

  public get isFilterProcessing(): boolean {
    return this._isFilterProcessing;
  }

  protected filterProducts(filteredProducts: ProductData[]): void {
    this._isFilterProcessing = true;
    this.state.data.filteredTableRows = filteredProducts;
    this.pagedFunds.records = filteredProducts;
    this.sendNext('filterRows');
    this._isFilterProcessing = false;
  }

  public goToPage(n: number): void {
    if (this.pagedFunds) {
      this.pagedFunds.pageNumber = n;
    }
  }

  public getNumberOfPages$(): Observable<number> {
    return this.pagedFunds.numberOfPages$;
  }

  public getPageNumber$(): Observable<number> {
    return this.pagedFunds.pageNumber$;
  }

  public getCurrentPage$(): Observable<ProductData[]> {
    return this.pagedFunds.currentPage$;
  }

  /**
   * send update to observers
   * @param actionName - to give guidance to Observers as to the nature of the change
   */
  protected sendNext(actionName: string) {
    this.state.lastAction = actionName;
    this.subject.next(this.state);
  }

  public subscribe(subscriber) {
    return this.subject.subscribe(subscriber);
  }

  public getProductType(): ConfigurationId {
    return this.productType;
  }

  protected getProductTypeParameter(
    configurationId: ConfigurationId
  ): ProductTypeParameter {
    switch (configurationId) {
      case ConfigurationId.MF:
        return ProductTypeParameter.MUTUAL_FUNDS;
      case ConfigurationId.MMF:
        return ProductTypeParameter.MONEY_MARKET_FUNDS;
      case ConfigurationId.ETF:
        return ProductTypeParameter.ETFS;
      case ConfigurationId.CEF:
        return ProductTypeParameter.CLOSED_END_FUNDS;
      case ConfigurationId.TIF:
        return ProductTypeParameter.TIFs;
      case ConfigurationId.VIP:
        return ProductTypeParameter.VARIABLE_INSURANCES;
      case ConfigurationId._529:
        return ProductTypeParameter.PORTFOLIOS_529;
      case ConfigurationId.SMA:
        return ProductTypeParameter.SMAs;
      case ConfigurationId.IF:
        return ProductTypeParameter.INTERVAL_FUNDS;
      case ConfigurationId.TOF:
        return ProductTypeParameter.TENDER_OFFER_FUNDS;
      case ConfigurationId.UAFF:
        return ProductTypeParameter.UAE_FEEDER_FUNDS;
      case ConfigurationId.CIT:
        return ProductTypeParameter.COLLECTIVE_INVESTMENT_TRUST;
      default:
        logger.error('unknown product type: ', configurationId);
    }
    throw Error('unknown product type: ' + configurationId);
  }

  /**
   * Flatten the productsData against their sharedClasses.
   * @param products ppss data from feed
   */
  protected getProductsData(products: Product[]): ProductData[] {
    return this.sortProductsData(
      products
        .map((product) => {
          return product.shareClasses.map((shareclass) => {
            if (!this.primaryShareClassMap.get(product.fundId)) {
              // WDE-5785 - (For showing the ETF-Series PDF)
              if (shareclass.primaryShareClass || shareclass.isEtfClass) {
                this.primaryShareClassMap.set(
                  product.fundId,
                  shareclass.shareClassCode
                );
              }
            }
            const { shareClasses, ...productRef } = product;
            const productData: ProductData = productRef;
            productData.shareClass = shareclass;
            return productData;
          });
        })
        .flat()
    );
  }

  /**
   * DEFECT-FIX: NGC-3045.
   * Sort the fund listing based upon full fund name column.
   * UPDATED-FIX: NGC-8947.
   * @param products fund listing
   */
  private sortProductsData(products: ProductData[]): ProductData[] {
    if (this.productType === ConfigurationId._529) {
      products = products.sort((current, next) => {
        if (current.fundNameForSorting !== next.fundNameForSorting) {
          return this.sortAlphaNum(
            current.fundNameForSorting,
            next.fundNameForSorting
          );
        } else {
          return this.sortAlphaNum(
            current.shareClass.shareClassName,
            next.shareClass.shareClassName
          );
        }
      });
      return products;
    } else if (this.productType === ConfigurationId.SMA) {
      products.sort((a, b) => {
        // to apply custom sort when string contains 'number-number' pattern
        if (a.fundName.match(/\d+-\d+/g) || b.fundName.match(/\d+-\d+/g)) {
          const fundNameCompVal = this.sortAlphaNum(a.fundName, b.fundName);
          return fundNameCompVal;
        } else {
          const fundNameCompVal = a.fundName.localeCompare(b.fundName);

          if (fundNameCompVal === 0) {
            return a.shareClass.shareClassName.localeCompare(
              b.shareClass.shareClassName
            );
          }

          return fundNameCompVal;
        }
      });
      return products;
    } else {
      products.sort((a, b) => {
        const fundNameCompVal = a.fundName.localeCompare(b.fundName);

        if (fundNameCompVal === 0) {
          return a.shareClass.shareClassName.localeCompare(
            b.shareClass.shareClassName
          );
        }

        return fundNameCompVal;
      });
      return products;
    }
  }

  protected sortByFavFund(products: ProductData[]): ProductData[] {
    return products.sort((a, b) => {
      return a.shareClass.isFavoriteFund === b.shareClass.isFavoriteFund
        ? 0
        : a.shareClass.isFavoriteFund
        ? -1
        : 1;
    });
  }

  /**
   * DEFECT-FIX: NGC-8947.
   * Sort the full fund name column with alphanumeric values.
   * @param products fund listing
   */
  private sortAlphaNum(a: string, b: string): number {
    const reA = /[^a-zA-Z]/g;
    const reN = /[^0-9]/g;
    const aA = a.replace(reA, '');
    const bA = b.replace(reA, '');
    if (aA === bA) {
      const aN = parseInt(a.replace(reN, ''), 10);
      const bN = parseInt(b.replace(reN, ''), 10);
      return aN === bN ? 0 : aN > bN ? 1 : -1;
    } else {
      return aA > bA ? 1 : -1;
    }
  }

  /**
   * Fetch the appropriate query as per decorated config.
   */
  protected fetchQueries(): DocumentNode[] {
    if (this.isSiteIntl) {
      switch (this.getProductType()) {
        case ConfigurationId.MF: // fall through
        case ConfigurationId.MMF: // fall through
        case ConfigurationId.UAFF: // fall through
        case ConfigurationId.ETF:
          const ret = [
            ppssInternationalCoreQuery,
            ppssInternationalIdentifiersQuery,
            ppssCommonPerformanceQuery,
            ppssInternationalRatingsQuery,
          ];
          this.state.config.performanceViewOptions.forEach((option) => {
            option.periodEnds.forEach((period) => {
              ret.push(QUERIES[`${option.name}_${period}`]);
            });
          });
          return ret;
      }
    } else {
      switch (this.getProductType()) {
        case ConfigurationId.MF: // fall through
        case ConfigurationId.ETF: // fall through
        case ConfigurationId.MMF: // fall through
        case ConfigurationId.IF: // fall through
        case ConfigurationId.TIF: // fall through
        case ConfigurationId.TOF:
        case ConfigurationId.CIT:
          return [
            ppssUsCoreQuery,
            ppssUsIndentifiers,
            ppssCommonPerformanceQuery,
            ppssAvAnnualMonthlyQuery,
            ppssAvAnnualQuarterlyQuery,
            ppssUsYieldsQuery,
            ppssUsRatingsQuery,
          ];
        case ConfigurationId.CEF:
          return [ppssUsCefQuery];
        case ConfigurationId.VIP:
          return [ppssUsVipQuery];
        case ConfigurationId._529:
          return [ppssUs529Query];
        case ConfigurationId.SMA:
          return [ppssUsSmaQuery];
      }
    }
  }

  public mapDocumentAtFundLevel(
    processedDocs: LiteratureDocument[],
    fundRow: ProductData
  ): LiteratureDocument[] {
    const fundAllDocsProductProfile = [];

    let fundAllDocs = processedDocs?.filter((doc) => {
      // If isMappedLanguage is false then this will ignore the doc.
      if (!this.isMappedLanguage(doc)) {
        return false;
      }

      const isOneTisExist = doc.frkOneTISNumber?.includes(fundRow.fundId);

      if (
        (this.getProductType() === ConfigurationId.SMA ||
          fundRow?.investmentManager?.toLowerCase().includes('franklin') ||
          this.getProductType() === ConfigurationId.TIF) &&
        isOneTisExist &&
        doc.dctermsType === LiteratureDocType.PRODUCT_PROFILE
      ) {
        fundAllDocsProductProfile.push(doc);
      }
      return isOneTisExist && doc.dctermsType === LiteratureDocType.FACT_SHEET;
    });

    if (
      ((this.getProductType() === ConfigurationId.SMA ||
        fundRow?.investmentManager?.toLowerCase().includes('franklin')) &&
        fundAllDocs?.length === 0) ||
      this.getProductType() === ConfigurationId.TIF
    ) {
      fundAllDocs = fundAllDocsProductProfile;
    }

    return fundAllDocs;
  }

  public getAvailableShareClasses(frkOneTISNumber: FundId[]): string[] {
    return frkOneTISNumber?.map((onTis) =>
      this.primaryShareClassMap.get(onTis)
    );
  }

  // This method checks weether it is a mapped language that matched the documents urls.
  private isMappedLanguage(litDocument: LiteratureDocument): boolean {
    /* This will return true always for the sites where language mapping is not set
    // to replicate the previous behaviour.
    */

    if (!this.siteConfigService.literature?.languageMapping?.length) {
      return true;
    }

    // The language mapping can not be validate if downloadUrl or literatureHref is not in the document.
    if (!(litDocument.downloadUrl || litDocument.literatureHref)) {
      return false;
    }

    return this.siteConfigService.literature.languageMapping?.some((lang) =>
      litDocument.dctermsLanguage
        ?.map((v) => v.toLowerCase())
        .includes(lang.toLowerCase())
    );
  }
}
